/* Claude 4.10/4.14/4.19 — BASKET DRAWER ADD-ONS.
 *
 *  4.10  Quantity on the packaging chips ("Option 1", the owner's pick from
 *        three mockups). A ticked chip grows a compact [− n +] before its
 *        price, capped at qualifying units: one gift box per bag, one
 *        protective box per box-worthy item. Also corrects the /cart page,
 *        which has hardcoded a "1" for these lines since 3.91.
 *
 *  4.12  The flash sale's OFFER band, rendered as an inert <template> in
 *        layout/theme.liquid and moved into the drawer from here.
 *
 *  4.14  THE BAND IS NOW THE ONLY BANNER THERE IS, so it shows on any
 *        non-empty basket. The owner cut the raffia prices directly and
 *        switched the automatic discount off, which means the section's own
 *        "earned" banner and the 15% OFF corner badges no longer fire at
 *        all: both are gated on final_line_price < original_line_price,
 *        i.e. a real ALLOCATED discount, and a lower price with a
 *        compare-at is not that. Until 4.12 the injector deferred to the
 *        earned banner and hid itself once a sale bag was in the basket;
 *        with nothing to defer to, that rule left a raffia shopper looking
 *        at no banner whatsoever. The earned-banner check below STAYS — it
 *        costs nothing and stops two bands stacking if an automatic
 *        discount is ever switched back on.
 *
 *  4.19  A CAP OF 1 NO LONGER GROWS A STEPPER. One bag in the basket means
 *        one gift box is the only legal quantity — floor and ceiling are
 *        both 1 — so the old code rendered a [− 1 +] with BOTH buttons
 *        permanently disabled. Correct, but indistinguishable from broken:
 *        reported as "the +/- doesn't work" (it never could, at cap 1, by
 *        design). Below cap<=0 is now cap<=1: a stepper only appears when
 *        there is an actual range to move through. At cap 1 the chip still
 *        ticks and still shows its price; it just doesn't grow dead controls.
 *
 * ============================================================
 * WHY ANY OF THIS IS IN A SEPARATE FILE, AND WHAT IT COSTS.
 * The drawer section is ~90KB and its script ~50KB. Every edit through the
 * Admin API rewrites a file WHOLE, and neither of those can be retransmitted
 * in one write — quite apart from this theme having already been bitten once
 * by a large-file write coming back mangled (the reason
 * assets/claude-packbox.css exists at all). So both features live here.
 *
 * THE FLASH HALF IS A STOPGAP AND SHOULD NOT OUTLIVE THE SALE IT WAS BUILT
 * FOR. Rendering the band from layout/theme.liquid means the sale's copy and
 * end date are written there rather than read from the drawer section's own
 * flash_* settings — a second source of truth, which is exactly what the
 * data attributes added in 3.91/3.98b exist to prevent. The right home is
 * sections/claude-cart-drawer.liquid, beside the earned banner it mirrors. A
 * ready-made patch for that lives in the project docs
 * (raffia-flash-sale-plan). When it is applied, delete BOTH the template in
 * theme.liquid and the flash half of this file.
 * ============================================================
 *
 * ============================================================
 * NOTHING HERE MAY ASSUME THE DOM SURVIVES A CART CHANGE.
 * swapSections() in claude-cart-drawer.js replaces #ClaudeCartDrawer-Inner
 * wholesale on every cart request, so anything injected is destroyed on every
 * tick, every stepper press and every quantity change. That is why this file
 * paints from a MutationObserver rather than once on load — the same lesson
 * the drawer's own error line and discount note had to learn (3.63, 3.85).
 * ============================================================
 *
 * NO SECOND SOURCE OF TRUTH FOR WHAT QUALIFIES (the 4.10 half). The handles
 * and the type/keyword lists are read from the data- attributes the section
 * already writes on #ClaudeCartDrawer-Inner — the same ones sweepAddons()
 * reads. Do not reintroduce a literal list here.
 *
 * THE STEPPER SITS INSIDE A <label>, so a plain click would toggle the tick
 * and remove the box. The document-level listener below runs in the CAPTURE
 * phase and calls preventDefault + stopPropagation, which kills both the
 * label's activation of its checkbox AND the drawer's own delegated click
 * handler further down. Move it to the bubble phase and pressing + removes
 * the very box you are counting.
 */
(function () {
  'use strict';

  var PAINT_DEBOUNCE = 60;
  var busy = false; // set while WE are writing to the cart
  var timer = null;

  function drawerEl() { return document.querySelector('cart-drawer.ccd'); }
  function innerEl() { return document.querySelector('#ClaudeCartDrawer-Inner'); }

  function csv(el, key) {
    return String((el && el.dataset && el.dataset[key]) || '')
      .toLowerCase()
      .split(',')
      .map(function (s) { return s.trim(); })
      .filter(Boolean);
  }

  /* Units, not lines: two bags on one line still earn two gift boxes.
     The bag exclusion in the pack test is load-bearing and mirrors the
     Liquid — several bags are titled "... Cowhide Handbag" and would
     otherwise match the "cowhide" keyword. */
  function computeCaps(cart, inn) {
    var addons = csv(inn, 'addonHandles');
    var packTypes = csv(inn, 'packboxTypes');
    var packWords = csv(inn, 'packboxKeywords');
    var caps = { gift: 0, pack: 0 };
    (cart.items || []).forEach(function (i) {
      var handle = String(i.handle || '').toLowerCase();
      if (addons.indexOf(handle) > -1) return;
      var type = String(i.product_type || '').toLowerCase();
      if (type === 'bag') { caps.gift += i.quantity; return; }
      var title = String(i.product_title || i.title || '').toLowerCase();
      var wants = packTypes.indexOf(type) > -1 || packWords.some(function (w) { return title.indexOf(w) > -1; });
      if (wants) caps.pack += i.quantity;
    });
    return caps;
  }

  function fetchCart() {
    var d = drawerEl();
    if (d && typeof d.fetchCart === 'function') return d.fetchCart();
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .catch(function () { return null; });
  }

  function stepperHtml(qty, cap, line, label) {
    var atMin = qty <= 1;
    var atMax = qty >= cap;
    return (
      '<span class="ccd-chipqty" data-ccd-addon-qty>' +
      '<button type="button" class="ccd-chipqty-btn" data-ccd-addon-to="' + (qty - 1) + '" data-ccd-addon-line="' + line + '"' +
      (atMin ? ' disabled' : '') + ' aria-label="Decrease number of ' + label + '">&minus;</button>' +
      '<span class="ccd-chipqty-val">' + qty + '</span>' +
      '<button type="button" class="ccd-chipqty-btn" data-ccd-addon-to="' + (qty + 1) + '" data-ccd-addon-line="' + line + '"' +
      (atMax ? ' disabled' : '') + ' aria-label="Increase number of ' + label + '">+</button>' +
      '</span>'
    );
  }

  function paint() {
    fixCartPage();
    if (busy) return;
    var inn = innerEl();
    if (!inn) return;

    var chips = [].slice.call(inn.querySelectorAll('.ccd-ctl .ccd-addon.ccd-chip'));
    var ticked = chips.filter(function (c) {
      var box = c.querySelector('.ccd-addon-check');
      return box && box.checked;
    });
    var wantsFlash = !!document.getElementById('ClaudeFlashOffer');

    if (!ticked.length && !wantsFlash) {
      chips.forEach(stripStepper);
      stripFlashOffer(inn);
      return;
    }

    fetchCart().then(function (cart) {
      if (!cart || !cart.items) return;

      paintFlashOffer(inn, cart);

      if (!ticked.length) { chips.forEach(stripStepper); return; }

      var caps = computeCaps(cart, inn);
      var giftHandle = String(inn.dataset.giftHandle || 'luxury-gift-box').toLowerCase();
      var overs = [];

      chips.forEach(function (chip) {
        var box = chip.querySelector('.ccd-addon-check');
        if (!box || !box.checked) { stripStepper(chip); return; }
        var variant = parseInt(box.dataset.variant, 10);
        var idx = cart.items.findIndex(function (i) { return i.variant_id === variant; });
        if (idx < 0) { stripStepper(chip); return; }

        var item = cart.items[idx];
        var handle = String(item.handle || '').toLowerCase();
        var cap = handle === giftHandle ? caps.gift : caps.pack;
        var qty = item.quantity;

        /* A basket can drift over the cap without anyone touching this
           control — buy two bags, take two gift boxes, then step the bags
           back to one. Correct it rather than render an impossible number. */
        if (cap > 0 && qty > cap) { overs.push({ line: idx + 1, cap: cap }); return; }
        /* 4.19: cap<=1 means there is no range to step through (floor and
           ceiling both 1) — a stepper here would be two permanently-disabled
           buttons, which reads as broken rather than as "nothing to do". */
        if (cap <= 1) { stripStepper(chip); return; }

        var label = handle === giftHandle ? 'gift boxes' : 'protective boxes';
        render(chip, stepperHtml(qty, cap, idx + 1, label));
      });

      if (overs.length) clampDown(overs[0]);
    });
  }

  function render(chip, html) {
    var existing = chip.querySelector('[data-ccd-addon-qty]');
    if (existing && existing.outerHTML === html) return; // idempotent: our own mutations must not loop
    if (existing) existing.remove();
    var amt = chip.querySelector('.ccd-chip-amt');
    if (amt) amt.insertAdjacentHTML('beforebegin', html);
    else chip.insertAdjacentHTML('beforeend', html);
  }

  function stripStepper(chip) {
    var s = chip.querySelector('[data-ccd-addon-qty]');
    if (s) s.remove();
  }

  /* ---------------------------------------------------------------
     THE FLASH SALE BAND.

     4.14: shown on ANY non-empty basket (see the header). Two things still
     keep it honest:

     1. It defers to the section's own banner. If the section has rendered
        the earned band — which needs a real allocated discount — this does
        nothing, so there is never one of each. That path is dormant while
        the discount is off, and costs nothing to keep.
     2. It disappears with the sale. theme.liquid only renders the template
        while the end date is in the future, so past it there is nothing to
        inject and this no-ops. Nothing to remove by hand.

     The band's WORDING is the thing to watch, not the code: it quotes a
     percentage and a deadline that live in theme.liquid, and neither can
     check itself against the products. If the prices move, move the copy.
     --------------------------------------------------------------- */
  function stripFlashOffer(inn) {
    var el = inn && inn.querySelector('[data-ccd-flash-offer]');
    if (el) el.remove();
  }

  function paintFlashOffer(inn, cart) {
    var tpl = document.getElementById('ClaudeFlashOffer');
    if (!tpl || !tpl.content) { stripFlashOffer(inn); return; }

    var items = cart.items || [];
    /* The section's own band, if it is there. Ours carries the wrapper
       attribute, so this only ever matches the earned one. */
    var earned = inn.querySelector('.ccd-flashbanner:not([data-ccd-flash-offer] .ccd-flashbanner)');

    if (!items.length || earned) { stripFlashOffer(inn); return; }
    if (inn.querySelector('[data-ccd-flash-offer]')) return; // already painted — do not loop

    var header = inn.querySelector('.ccd-header');
    if (!header) return;
    var wrap = document.createElement('div');
    wrap.setAttribute('data-ccd-flash-offer', '');
    wrap.appendChild(tpl.content.cloneNode(true));
    header.insertAdjacentElement('afterend', wrap);
  }

  function change(line, qty) {
    var d = drawerEl();
    if (!d || typeof d.changeLine !== 'function') return;
    busy = true;
    Promise.resolve(d.changeLine(line, qty)).catch(function () {}).then(function () {
      busy = false;
      schedule();
    });
  }

  function clampDown(over) { change(over.line, over.cap); }

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(paint, PAINT_DEBOUNCE);
  }

  /* CAPTURE phase — see the header. This must beat both the <label> and the
     drawer's own delegated onClick. */
  document.addEventListener(
    'click',
    function (e) {
      var btn = e.target && e.target.closest ? e.target.closest('.ccd-chipqty-btn') : null;
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      if (btn.disabled || busy) return;
      var line = parseInt(btn.dataset.ccdAddonLine, 10);
      var to = parseInt(btn.dataset.ccdAddonTo, 10);
      if (!line || Number.isNaN(to) || to < 1) return; // 1 is the floor; untick removes
      change(line, to);
    },
    true
  );

  /* ---------------------------------------------------------------
     /cart: SAY HOW MANY BOXES ARE ACTUALLY IN THE BASKET.
     sections/main-cart-items.liquid renders add-on lines with a LITERAL "1"
     in place of Dawn's stepper (3.91), because until now one was the only
     quantity these lines could hold. That is no longer true, and a shopper
     reviewing /cart before paying would read "1" on a line whose price and
     the order total both say two. The money was never wrong — the hidden
     updates[] input beside it carries the real quantity, which is also where
     we read it from here — but the number a customer checks was.

     Display only: the line stays non-editable on /cart, because the cap that
     makes editing safe is computed in the drawer.

     The proper fix is one Liquid expression ({{ item.quantity }} in place of
     the two hardcoded 1s in main-cart-items.liquid). It lives here instead
     for the same file-size reason given at the top of this file.
     --------------------------------------------------------------- */
  function fixCartPage() {
    var spans = document.querySelectorAll('.ccd-addon-qty-fixed');
    if (!spans.length) return;
    [].forEach.call(spans, function (el) {
      var wrap = el.parentElement;
      if (!wrap) return;
      var hidden = wrap.querySelector('input[type="hidden"][name="updates[]"]');
      if (!hidden) return;
      var qty = parseInt(hidden.value, 10);
      if (!qty || qty < 1) return;
      if (el.textContent.trim() !== String(qty)) el.textContent = qty;
      var sr = wrap.querySelector('.visually-hidden');
      if (sr) sr.textContent = sr.textContent.replace(/\d+\s*$/, String(qty));
    });
  }

  function start() {
    /* Dawn re-renders the /cart line items over AJAX, so the corrected
       number has to survive that too. */
    var cartItems = document.querySelector('cart-items');
    if (cartItems) new MutationObserver(schedule).observe(cartItems, { childList: true, subtree: true });

    var d = drawerEl();
    if (d) new MutationObserver(schedule).observe(d, { childList: true, subtree: true });
    schedule();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
